import { useEffect, useRef, useState } from 'react'
import type { AiAspectRatio, AiGenerationRecord, AiImageResult, MediaAssetSummary } from '@cms/contracts'
import { ApiError, newIdempotencyKey } from '../api/client.js'
import { generateImage } from '../api/ai.js'
import { getSite } from '../api/endpoints.js'
import { ASPECT_OPTIONS, PROMPT_MAX, promptProblem } from '../domain/ai-generation.js'
import { useI18n } from '../i18n/context.js'
import { useSiteWorkspace } from '../workspace/site.js'
import { Checkbox, SelectField, TextArea, TextField } from '../ui/fields.js'
import { Dialog } from '../ui/overlays.js'
import { Banner, Button, LinearProgress, cx } from '../ui/primitives.js'
import { AiAccountSelect } from './AiAccountSelect.js'
import { MediaThumb } from './MediaBrowser.js'
import './ai-generation.css'

interface Attempt {
  key: string
  result: AiImageResult
  prompt: string
}

/**
 * "Genera con AI" for images (document/64 §2). The prompt, the format and the
 * account go to the gateway, which asks AI and puts the picture in the media
 * library with its prompt. Every attempt stays on screen with the prompt that
 * made it, so a variant can be compared with the one before and any of them
 * can be the one used; none is ever overwritten.
 *
 * `variantOf` starts the dialog as a regeneration of an existing image.
 */
export function AiImageDialog({ initialPrompt = '', variantOf = null, initialAspect, onUse, onClose, onGenerated }: {
  initialPrompt?: string
  variantOf?: string | null
  initialAspect?: AiAspectRatio
  /** "Usa questa immagine": the dialog closes and the caller takes the asset. */
  onUse?: (asset: MediaAssetSummary) => void
  onClose: () => void
  /** Every new asset, as soon as it exists (the library refreshes its grid). */
  onGenerated?: (result: AiImageResult) => void
}) {
  const { t, locale: uiLocale } = useI18n()
  const { siteId } = useSiteWorkspace()
  const [locale, setLocale] = useState<string>(uiLocale)
  const alive = useRef(true)
  // In sviluppo StrictMode monta, smonta e rimonta: senza rimettere `true` all’avvio la finestra restava per sempre su «Sto generando…».
  useEffect(() => { alive.current = true; return () => { alive.current = false } }, [])
  // The alt text is written in the site's own language, not the editor's.
  useEffect(() => {
    getSite(siteId).then((detail) => { if (alive.current && detail.defaultLocale) setLocale(detail.defaultLocale) }).catch(() => undefined)
  }, [siteId])

  const [prompt, setPrompt] = useState(initialPrompt)
  const [aspect, setAspect] = useState<AiAspectRatio>(initialAspect ?? '16:9')
  const [accountId, setAccountId] = useState('')
  const [name, setName] = useState('')
  const [alt, setAlt] = useState('')
  const [archivePrevious, setArchivePrevious] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [attempts, setAttempts] = useState<Attempt[]>([])
  const [selected, setSelected] = useState(0)
  const [history, setHistory] = useState<AiGenerationRecord['history']>([])

  const problem = promptProblem(prompt)
  const current = attempts[selected] ?? null
  const parent = attempts.length > 0 ? attempts[attempts.length - 1]!.result.id : variantOf

  const generate = async () => {
    if (problem || busy) return
    setBusy(true)
    setError(null)
    try {
      const result = await generateImage(siteId, {
        prompt: prompt.trim(),
        aspectRatio: aspect,
        ...(accountId ? { accountId } : {}),
        ...(name.trim() ? { name: name.trim() } : {}),
        ...(alt.trim() ? { alt: { [locale]: alt.trim() } } : {}),
        ...(parent ? { variantOf: parent, archivePrevious } : {}),
        reason: t('aiGen.image.reason', { prompt: prompt.trim().slice(0, 80) }),
        expectedVersion: 0,
      }, newIdempotencyKey())
      onGenerated?.(result)
      if (!alive.current) return
      setAttempts((list) => [...list, { key: result.id, result, prompt: prompt.trim() }])
      setSelected(attempts.length)
      if (result.generation) setHistory(result.generation.history)
    } catch (err) {
      if (alive.current) setError(err instanceof ApiError ? err.message : t('errors.network'))
    } finally {
      if (alive.current) setBusy(false)
    }
  }

  const use = () => {
    if (!current) return
    onUse?.({ ...current.result.asset, updatedAt: Date.now() })
    onClose()
  }

  return <Dialog
    title={variantOf ? t('aiGen.image.titleVariant') : t('aiGen.image.title')}
    icon="auto_awesome"
    size="lg"
    onClose={onClose}
    className="ai-dialog"
    actions={<>
      <span className="spacer"/>
      <Button variant="text" onClick={onClose}>{current ? t('common.close') : t('common.cancel')}</Button>
      <Button variant={current ? 'tonal' : 'filled'} icon="auto_awesome" disabled={!!problem || busy} onClick={() => void generate()}>
        {busy ? t('aiGen.image.generating') : current ? t('aiGen.image.regenerate') : t('aiGen.image.generate')}
      </Button>
      {current && onUse && <Button variant="filled" icon="check" disabled={busy} onClick={use}>{t('aiGen.image.use')}</Button>}
    </>}
  >
    <div className="ai-dialog-layout">
      <form className="form-stack" onSubmit={(event) => { event.preventDefault(); void generate() }}>
        <TextArea
          label={t('aiGen.common.prompt')}
          rows={5}
          value={prompt}
          disabled={busy}
          maxLength={PROMPT_MAX + 200}
          error={problem === 'long' ? t('aiGen.common.promptLong', { max: PROMPT_MAX }) : undefined}
          supporting={problem === 'short' && prompt.length > 0 ? t('aiGen.common.promptShort') : t('aiGen.image.promptHelp')}
          counter={<span className="counter">{prompt.trim().length}/{PROMPT_MAX}</span>}
          onChange={(event) => setPrompt(event.target.value)}
        />
        <div className="ai-row">
          <SelectField label={t('aiGen.common.aspect')} value={aspect} disabled={busy} onChange={(event) => setAspect(event.target.value as AiAspectRatio)}>
            {ASPECT_OPTIONS.map((ratio) => <option key={ratio} value={ratio}>{t(`aiGen.aspect.r${ratio.replace(':', 'x')}`)}</option>)}
          </SelectField>
          <AiAccountSelect purpose="image" value={accountId} onChange={setAccountId} disabled={busy}/>
        </div>
        <TextField label={t('aiGen.image.name')} value={name} disabled={busy} maxLength={200} supporting={t('aiGen.image.nameHelp')} onChange={(event) => setName(event.target.value)}/>
        <TextField label={t('aiGen.image.alt', { lang: locale.toUpperCase() })} value={alt} disabled={busy} maxLength={500} supporting={t('aiGen.image.altHelp')} onChange={(event) => setAlt(event.target.value)}/>
        {parent && <Checkbox label={t('aiGen.image.archivePrevious')} supporting={t('aiGen.image.archivePreviousHelp')} checked={archivePrevious} disabled={busy} onChange={(event) => setArchivePrevious(event.target.checked)}/>}
      </form>

      <div className="ai-result" aria-live="polite">
        {error && <Banner tone="error" title={t('aiGen.common.failed')}>{error}</Banner>}
        {busy && <div className="ai-working">
          <LinearProgress label={t('aiGen.image.generating')}/>
          <p className="muted">{t('aiGen.image.generatingHelp')}</p>
        </div>}
        {!busy && !current && !error && <p className="muted ai-empty">{t('aiGen.image.empty')}</p>}
        {current && <>
          <div className="ai-preview">
            <MediaThumb asset={current.result.asset} large/>
          </div>
          <p className="ai-caption">
            <strong>{current.result.asset.name}</strong>
            <span className="muted"> · {current.result.asset.width}×{current.result.asset.height}{current.result.accountUsed ? ` · ${current.result.accountUsed}` : ''}</span>
          </p>
          {current.result.deduplicated && <Banner tone="info">{t('aiGen.image.deduplicated')}</Banner>}
          <p className="ai-prompt-used"><span className="muted">{t('aiGen.image.promptUsed')}</span> {current.prompt}</p>
        </>}
        {(attempts.length > 1 || history.length > 0) && <section className="ai-history" aria-label={t('aiGen.image.history')}>
          <h3 className="ai-subtitle">{t('aiGen.image.history')}</h3>
          <ul>
            {history.filter((step) => !attempts.some((attempt) => attempt.result.id === step.assetId)).map((step) => <li key={step.assetId} className="ai-history-item is-earlier">
              <span className="ai-history-text">{step.prompt}</span>
              <span className="muted ai-history-meta">{t('aiGen.image.earlier')}</span>
            </li>)}
            {attempts.map((attempt, index) => <li key={attempt.key}>
              <button type="button" className={cx('ai-history-item', index === selected && 'is-selected')} aria-pressed={index === selected} onClick={() => setSelected(index)}>
                <span className="ai-history-text">{attempt.prompt}</span>
                <span className="muted ai-history-meta">{t('aiGen.image.attempt', { n: index + 1 })}</span>
              </button>
            </li>)}
          </ul>
        </section>}
      </div>
    </div>
  </Dialog>
}
